import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/app.state';
import { ApiStatus } from 'src/app/core/api/api.models';
import { ApiService } from 'src/app/core/api/api.service';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { LocaleService } from 'src/app/core/locale/locale.service';
import { showNotificationAction } from 'src/app/core/notification/notification.actions';
import { NotificationType } from 'src/app/core/notification/notification.state';
import { routeTo } from 'src/app/core/routing/routing.actions';
import { DynamicFormControl } from 'src/app/feature/dynamic-form/input/dynamic-form-control';
import { ControlType, COUNTRY_CODE } from 'src/app/feature/dynamic-form/input/dynamic-form-control.models';
import { COUNTRIES } from 'src/app/feature/dynamic-form/utilities/country-properties';
import { CountryProperties } from 'src/app/feature/dynamic-form/utilities/country-props.models';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { APPCONFIG_ENHANCED_PHONE_NUMBER } from 'src/app/shared/enums/application-config.enums';
import { Dictionary } from 'src/app/shared/types/dictionary';
import { validObject } from 'src/app/shared/utilities/types.utils';
import { routeToOrdersAction } from '../orders/orders.actions';
import { clearRecoverStateAction, PostVerifyAction, setOrderSearchRecoverCredentialsAction } from './recover.actions';
import { DynamicFormComponent } from 'src/app/feature/dynamic-form/dynamic-form.component';
import { NotificationFocusService } from 'src/app/core/services/notification-focus.service';

@Component({
	selector: 'ahp-recover',
	templateUrl: './recover.component.html',
	styleUrls: ['./recover.component.scss'],
})
export class RecoverComponent implements OnInit, AfterViewInit, OnDestroy {
	/**
	 * Our login form configuration
	 */
	public recoverFormControls: DynamicFormControl<any>[][] = [];
	/**
	 * If we're authenticating
	 */
	public isAuthenticating$: Observable<boolean>;
	/**
	 * If the form has been submitted.
	 */
	public recoverLoginSubmitted = false;
	/**
	 * If the form will be setup for enhanced phone number.
	 */
	public enhancedPhoneNumber: EnhancedPhoneNumber;
	/**
	 * The countries array.
	 */
	public countries: CountryProperties[] = COUNTRIES;
	/**
	 * The name of control type
	 */
	public phoneControlType: ControlType = 'phone';
	/**
	 * This post fix helps to manage the different country codes for each dropdown
	 */
	public countryCodePostFix = COUNTRY_CODE;
	/**
	 * Our subject to modify the observable if we're authenticating.
	 */

	private _isAuthenticating = new Subject<boolean>();
	/**
	 * For unsubscribing to observables when the component destroys.
	 */
	private _destroy$ = new Subject<void>();
	/**
	 * Recover Login form data
	 */
	private _recoverLoginFormData: Dictionary<any> = null;
	/**
	 * Dynamic Form that contains the address info
	 */
	@ViewChild('recoverForm') recoverForm: DynamicFormComponent;

	/**
	 * The constructor
	 * @param _store Our main store
	 * @param _apiService our API Service
	 * @param _locale our locale Service
	 * @param _title the current title
	 * @param _notificationFocusService returns focus after closing a notification
	 */
	constructor(
		private _store: Store<AppState>,
		private _apiService: ApiService,
		private _locale: LocaleService,
		private _title: Title,
		private _notificationFocusService: NotificationFocusService
	) {
		this._title.setTitle(_locale.get('recover.pagetitle'));

		this.isAuthenticating$ = this._isAuthenticating.asObservable();
	}

	/**
	 * The initialization function
	 */
	public ngOnInit(): void {
		// Clear the recover state first
		this._store.dispatch(clearRecoverStateAction());

		history.pushState(null, null, window.location.href);
		this._store
			.pipe(takeUntil(this._destroy$), select(selectAppConfig([APPCONFIG_ENHANCED_PHONE_NUMBER])))
			.subscribe((appConfigs) => {
				const { enhanced_phone_number } = appConfigs;
				if (validObject(enhanced_phone_number)) {
					this.enhancedPhoneNumber = enhanced_phone_number;
				}
			});
		this.recoverFormControls = this._getRecoverForm();
	}

	ngAfterViewInit(): void {
		this._notificationFocusService.setFocusedElement(this.recoverForm.textInputs);
	}

	/**
	 * Handles cleanup when the component is being destroyed
	 */
	public ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Called when our form data changes
	 * @param data The form data
	 */
	public recoverLoginFormChanged(data: Dictionary<any>): void {
		this._recoverLoginFormData = data.valid ? data : null;
	}

	/**
	 * Handles when the form is submitted
	 */
	public submitRecoverLogin(): void {
		this.recoverLoginSubmitted = true;

		if (!this._recoverLoginFormData) {
			return;
		}

		this._isAuthenticating.next(true);

		this._apiService
			.post(ApiRequestType.GUEST_ORDER_SEARCH, {
				email: this._recoverLoginFormData.value.email,
				phone_number: this._recoverLoginFormData.value.phone,
			})
			.pipe(take(1))
			.subscribe((response) => {
				if (response.SERVICE.status === 'FAILED') {
					this._isAuthenticating.next(false);
				}

				if (response.SERVICE.status === ApiStatus.OK || response.SERVICE.http_response_code === '401') {
					// Save information in recover state
					this._store.dispatch(
						setOrderSearchRecoverCredentialsAction({
							payload: {
								...this._recoverLoginFormData.value,
								postVerifyAction: PostVerifyAction.Orders,
							},
						})
					);
				}

				if (response.SERVICE.status === ApiStatus.OK && response.SERVICE?.MATCH?.length) {
					const orders = response.SERVICE.MATCH;
					// route to Orders page
					this._store.dispatch(routeToOrdersAction({ payload: orders }));
					this._store.dispatch(routeTo({ payload: ['orders'] }));
				} else if (response.SERVICE.http_response_code === '401') {
					// route to MFA Verify Page
					this._store.dispatch(routeTo({ payload: ['verify'] }));
				} else {
					this._store.dispatch(
						showNotificationAction({
							buttonLabel: this._locale.get('common.close'),
							dialogType: NotificationType.GENERAL,
							initiator: `Recover: Failed`,
							message: this._locale.get(`errorcode.${response.SERVICE.error_code}`),
						})
					);
				}
			});
	}

	/**
	 * Returns the Order Search form layout
	 */
	private _getRecoverForm(): DynamicFormControl<any>[][] {
		return [
			[
				new DynamicFormControl<string>({
					autocomplete: 'email',
					value: '',
					key: 'email',
					type: 'email',
					label: 'common.email',
					controlType: 'text',
					validators: 'required,email',
					onEnterPressed: this.onEnterPress.bind(this),
					onKeyDownSpace: this._onKeyDownSpacePress.bind(this),
				}),
			],
			[
				new DynamicFormControl<string>({
					value: '',
					key: 'phone',
					label: 'common.phone',
					controlType: 'phone',
					validators: 'required,minLength:3,maxLength:15',
					type: 'tel',
					onEnterPressed: this.onEnterPress.bind(this),
				}),
			],
		];
	}

	/**
	 * Submit form on enter press
	 * @private
	 */
	public onEnterPress(): void {
		this.submitRecoverLogin();
	}

	/**
	 * Prevent users from entering a space
	 * @param $event
	 * @private
	 */
	private _onKeyDownSpacePress($event: KeyboardEvent): void {
		$event.preventDefault();
	}

	/**
	 * Make a phone when exist the country code
	 * @param phone it contains the phone number without country code
	 * @returns The phone number concatenated with country code
	 * todo: revisit if we need to keep this here, as it's currently unused
	 */
	private _makePhoneNumber(phone: string): string {
		if (this.enhancedPhoneNumber?.use_enhanced_phone_number) {
			return (
				this.countries.find(
					(country) =>
						country.code ===
						this._recoverLoginFormData.value[`${this.phoneControlType}${this.countryCodePostFix}`].code
				).countryCode + phone
			);
		}
		return phone;
	}
}
